import React, {useState} from "react";
import {Formik, Form} from "formik";
import * as Yup from "yup";
import {toast} from "react-toastify";
import Modal from "../../../components/Modal";
import InputText from "../../../components/Inputs/InputText";
import Button from "../../../components/Button";
import AdminTable, {AdminTableColumn} from "../components/AdminTable";
import ConfirmDialog from "../components/ConfirmDialog";
import {AdminPreOrder, AdminLink} from "../../../interfaces/admin";
import {
    useGetAdminPreOrdersQuery,
    useGetAdminPreOrderQuery,
    useCreateAdminPreOrderMutation,
    useUpdateAdminPreOrderMutation,
    useDeleteAdminPreOrderMutation,
    useDetachAdminLinkMutation,
} from "../../../storage/adminApi";
import localStyles from "../styles.module.scss";

const AdminPreOrders = () => {
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);
    const [sortBy, setSortBy] = useState("id");
    const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
    const [search, setSearch] = useState("");
    const [showForm, setShowForm] = useState(false);
    const [editTarget, setEditTarget] = useState<AdminPreOrder | null>(null);
    const [deleteTarget, setDeleteTarget] = useState<AdminPreOrder | null>(null);
    const [detailId, setDetailId] = useState<number | null>(null);

    const params: Record<string, any> = {page, per_page: pageSize, sort_by: sortBy, sort_order: sortOrder};
    if (search) params.search = search;

    const {data, isLoading} = useGetAdminPreOrdersQuery(params);
    const {data: detailData} = useGetAdminPreOrderQuery(detailId!, {skip: !detailId});
    const [createPreOrder] = useCreateAdminPreOrderMutation();
    const [updatePreOrder] = useUpdateAdminPreOrderMutation();
    const [deletePreOrder] = useDeleteAdminPreOrderMutation();
    const [detachLink] = useDetachAdminLinkMutation();

    const columns: AdminTableColumn[] = [
        {label: "ID", key: "id", sortable: true},
        {label: "User", key: "username", render: (value: string) => value ?? "-"},
        {label: "Name", key: "name"},
        {label: "Hash", key: "hash"},
        {
            label: "Links",
            key: "links_count",
            render: (value: number) => (
                <span className={`${localStyles.badge} ${localStyles.badgeInfo}`}>{value ?? 0}</span>
            ),
        },
        {label: "Created", key: "created_at", sortable: true},
        {
            label: "Actions",
            key: "actions",
            render: (_: any, row: AdminPreOrder) => (
                <div className={localStyles.actionsCell}>
                    <Button
                        id={`view-preorder-${row.id}`}
                        size="small"
                        background="bg-outline"
                        text="View"
                        click={(e) => {
                            e.stopPropagation();
                            setDetailId(row.id);
                        }}
                    />
                    <Button
                        id={`edit-preorder-${row.id}`}
                        size="small"
                        background="bg-primary"
                        text="Edit"
                        click={(e) => {
                            e.stopPropagation();
                            setEditTarget(row);
                            setShowForm(true);
                        }}
                    />
                    <Button
                        id={`delete-preorder-${row.id}`}
                        size="small"
                        background="bg-danger"
                        text="Delete"
                        click={(e) => {
                            e.stopPropagation();
                            setDeleteTarget(row);
                        }}
                    />
                </div>
            ),
        },
    ];

    const handleSubmit = async (values: {name: string}) => {
        try {
            if (editTarget) {
                await updatePreOrder({preOrderId: editTarget.id, data: values}).unwrap();
                toast.success("Pre-order updated");
            } else {
                await createPreOrder(values).unwrap();
                toast.success("Pre-order created");
            }
            setShowForm(false);
            setEditTarget(null);
        } catch (e) {
            toast.error("Operation failed");
        }
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        try {
            await deletePreOrder(deleteTarget.id).unwrap();
            toast.success("Pre-order deleted");
            setDeleteTarget(null);
        } catch (e) {
            toast.error("Failed to delete pre-order");
        }
    };

    const handleDetach = async (linkId: number) => {
        if (!detailId) return;
        try {
            await detachLink({preOrderId: detailId, linkId}).unwrap();
            toast.success("Link detached");
        } catch (e) {
            toast.error("Failed to detach link");
        }
    };

    return (
        <div>
            <div className={localStyles.pageHeader}>
                <h4>Pre-Orders</h4>
                <Button
                    id="create-preorder"
                    size="small"
                    background="bg-primary"
                    text="Create Pre-Order"
                    click={() => {
                        setEditTarget(null);
                        setShowForm(true);
                    }}
                />
            </div>

            <div className={localStyles.filterBar}>
                <div className={localStyles.filterGroup}>
                    <InputText
                        id="search-preorder"
                        placeholder="Search by name..."
                        value={search}
                        handleChange={(value: string) => {
                            setSearch(value);
                            setPage(1);
                        }}
                    />
                </div>
            </div>

            <AdminTable
                columns={columns}
                data={data?.items ?? []}
                total={data?.total ?? 0}
                page={page}
                pageSize={pageSize}
                onPageChange={setPage}
                onPageSizeChange={(size) => {
                    setPageSize(size);
                    setPage(1);
                }}
                loading={isLoading}
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSortChange={(newSortBy, newSortOrder) => {
                    setSortBy(newSortBy);
                    setSortOrder(newSortOrder);
                    setPage(1);
                }}
            />

            {/* Create/Edit Modal */}
            {showForm && (
                <Formik
                    initialValues={{name: editTarget?.name ?? ""}}
                    validationSchema={Yup.object().shape({
                        name: Yup.string().required("Name is required"),
                    })}
                    onSubmit={handleSubmit}
                >
                    {({values, errors, touched, isSubmitting, setFieldValue}) => (
                        <Modal
                            show
                            handleClose={() => {
                                setShowForm(false);
                                setEditTarget(null);
                            }}
                            width={450}
                            closeOut
                            element={
                                <div>
                                    <div className={localStyles.modalTitle}>
                                        {editTarget ? "Edit Pre-Order" : "Create Pre-Order"}
                                    </div>
                                    <Form>
                                        <div className={localStyles.modalBody}>
                                            <InputText
                                                id="preorder-name"
                                                labelText="Name"
                                                placeholder="Pre-order name"
                                                value={values.name}
                                                handleChange={(value: string) => setFieldValue("name", value)}
                                                errorMessage={touched.name && errors.name ? errors.name : ""}
                                            />
                                        </div>
                                        <div className={localStyles.modalFooter}>
                                            <Button
                                                id="preorder-form-cancel"
                                                size="small"
                                                background="bg-outline-grey"
                                                text="Cancel"
                                                click={() => {
                                                    setShowForm(false);
                                                    setEditTarget(null);
                                                }}
                                            />
                                            <Button
                                                id="preorder-form-submit"
                                                size="small"
                                                background="bg-primary"
                                                text={editTarget ? "Update" : "Create"}
                                                type="submit"
                                                disabled={isSubmitting}
                                            />
                                        </div>
                                    </Form>
                                </div>
                            }
                        />
                    )}
                </Formik>
            )}

            {/* Detail Modal */}
            {detailId && detailData && (
                <Modal
                    show
                    handleClose={() => setDetailId(null)}
                    width={700}
                    closeOut
                    element={
                        <div>
                            <div className={localStyles.modalTitle}>Pre-Order: {detailData.name}</div>
                            <div className={localStyles.detailField}>
                                <strong>Hash:</strong> {detailData.hash}
                            </div>
                            <div className={localStyles.detailField}>
                                <strong>Created:</strong> {detailData.created_at}
                            </div>
                            <div className={localStyles.sectionTitle}>Attached Links</div>
                            {detailData.links && detailData.links.length > 0 ? (
                                <table className={localStyles.detailTable}>
                                    <thead>
                                        <tr>
                                            <th>ID</th>
                                            <th>Tracknumber</th>
                                            <th>Data Type</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {detailData.links.map((link: AdminLink) => (
                                            <tr key={link.id}>
                                                <td>{link.id}</td>
                                                <td>{link.tracknumber}</td>
                                                <td>{link.data_type}</td>
                                                <td>
                                                    <Button
                                                        id={`detach-link-${link.id}`}
                                                        size="small"
                                                        background="bg-danger"
                                                        text="Detach"
                                                        click={() => handleDetach(link.id)}
                                                    />
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            ) : (
                                <p className={localStyles.textMuted}>No links attached</p>
                            )}
                            <div className={localStyles.modalFooter}>
                                <Button
                                    id="detail-close"
                                    size="small"
                                    background="bg-outline-grey"
                                    text="Close"
                                    click={() => setDetailId(null)}
                                />
                            </div>
                        </div>
                    }
                />
            )}

            <ConfirmDialog
                show={!!deleteTarget}
                title="Delete Pre-Order"
                message={`Are you sure you want to delete pre-order "${deleteTarget?.name}"?`}
                onConfirm={handleDelete}
                onCancel={() => setDeleteTarget(null)}
            />
        </div>
    );
};

export default <AdminPreOrders/>;
